import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ChevronLeft, RotateCcw, Plus, Minus } from "lucide-react";
import { SKILLS, pointsAt, totalCost, STORAGE_KEY } from "@/lib/skillsData";
import { renderMarkdown } from "@/lib/markdown";

const DEFAULT_LEVEL = 10;

function loadInitial() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (err) {
    console.warn("Skills: failed to read localStorage", err);
    return null;
  }
}

export default function Skills() {
  const initial = loadInitial();
  const [level, setLevel] = useState(initial?.level || DEFAULT_LEVEL);
  const [ranks, setRanks] = useState(initial?.ranks || {});

  // Persist
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ level, ranks }));
    } catch (err) {
      // Storage may be unavailable (private mode / quota exceeded) — non-fatal.
      console.warn("Skills: failed to persist to localStorage", err);
    }
  }, [level, ranks]);

  const available = pointsAt(level);
  const used = useMemo(() => totalCost(ranks), [ranks]);
  const remaining = available - used;

  const reset = () => setRanks({});

  const setRank = (key, r) => setRanks(prev => ({ ...prev, [key]: r }));

  const inc = (skill) => {
    const cur = ranks[skill.key] || 0;
    if (level < skill.unlock) return;
    if (cur >= skill.max) return;
    const nextCost = skill.cost(cur + 1);
    if (nextCost > remaining) return;
    setRank(skill.key, cur + 1);
  };

  const dec = (skill) => {
    const cur = ranks[skill.key] || 0;
    if (cur <= 0) return;
    setRank(skill.key, cur - 1);
  };

  const fight = SKILLS.filter(s => s.category === "Fight Skills");
  const economic = SKILLS.filter(s => s.category === "Economic Skills");

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-8 md:py-12" data-testid="skills-page">
      <Link to="/wiki" className="inline-flex items-center gap-1 text-[10px] md:text-xs font-mono tracking-[0.2em] text-[var(--t-blue-glow)] uppercase hover:text-[var(--t-cyan)] mb-4" data-testid="skills-back">
        <ChevronLeft className="w-3 h-3" /> Wiki
      </Link>

      <div className="flex items-end justify-between gap-4 mb-6 md:mb-8 flex-wrap">
        <div>
          <span className="font-mono text-[10px] md:text-xs tracking-[0.25em] md:tracking-[0.3em] text-[var(--t-blue-glow)] uppercase">// Skill Planner</span>
          <h1 className="font-display text-3xl md:text-5xl mt-2 text-[var(--t-text)] uppercase leading-tight">Skills</h1>
          <p className="text-sm text-[var(--t-text-dim)] mt-2">Allocate skill points by character level — live in-game math.</p>
        </div>
        <button onClick={reset} className="btn-ghost !py-2 !px-3 !text-xs" data-testid="skills-reset"><RotateCcw className="w-3.5 h-3.5" /> Reset</button>
      </div>

      {/* Stats bar */}
      <div className="panel panel-glow corner-frame p-4 md:p-5 mb-8 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="skills-stats-bar">
        <div>
          <div className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase mb-2">Character Level</div>
          <div className="flex items-center gap-2">
            <button onClick={() => setLevel(l => Math.max(1, l - 1))} className="w-7 h-7 inline-flex items-center justify-center border border-[var(--t-border-glow)] text-[var(--t-blue-glow)] hover:border-[var(--t-blue)] hover:text-[var(--t-cyan)]" data-testid="level-down" aria-label="Decrease level"><Minus className="w-3.5 h-3.5" /></button>
            <input type="number" min="1" max="50" value={level} onChange={(e) => setLevel(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))} className="field !py-1 !text-center !text-lg font-display w-20" data-testid="level-input" />
            <button onClick={() => setLevel(l => Math.min(50, l + 1))} className="w-7 h-7 inline-flex items-center justify-center border border-[var(--t-border-glow)] text-[var(--t-blue-glow)] hover:border-[var(--t-blue)] hover:text-[var(--t-cyan)]" data-testid="level-up" aria-label="Increase level"><Plus className="w-3.5 h-3.5" /></button>
          </div>
        </div>
        <div>
          <div className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase mb-2">Available</div>
          <div className="font-display text-2xl md:text-3xl text-[var(--t-cyan)]">{available}</div>
        </div>
        <div>
          <div className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase mb-2">Spent</div>
          <div className="font-display text-2xl md:text-3xl text-[var(--t-text)]">{used}</div>
        </div>
        <div>
          <div className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase mb-2">Remaining</div>
          <div className="font-display text-2xl md:text-3xl" style={{ color: remaining < 0 ? "var(--t-red)" : "var(--t-green)" }}>{remaining}</div>
        </div>
      </div>

      <SkillSection title="Fight Skills" skills={fight} ranks={ranks} level={level} remaining={remaining} onInc={inc} onDec={dec} />
      <SkillSection title="Economic Skills" skills={economic} ranks={ranks} level={level} remaining={remaining} onInc={inc} onDec={dec} />
    </div>
  );
}

function SkillSection({ title, skills, ranks, level, remaining, onInc, onDec }) {
  return (
    <section className="mb-10" data-testid={`section-${title.replace(/\s+/g, "-").toLowerCase()}`}>
      <h2 className="font-display text-xl md:text-2xl text-[var(--t-blue-glow)] uppercase tracking-[0.06em] mb-4 pb-2 border-b border-[var(--t-border)]">{title}</h2>
      <div className="space-y-3">
        {skills.map(s => <SkillRow key={s.key} skill={s} rank={ranks[s.key] || 0} level={level} remaining={remaining} onInc={onInc} onDec={onDec} />)}
      </div>
    </section>
  );
}

function SkillRow({ skill, rank, level, remaining, onInc, onDec }) {
  const Icon = Icons[skill.icon] || Icons.Star;
  const locked = level < skill.unlock;
  const maxed = rank >= skill.max;
  const nextCost = !maxed ? skill.cost(rank + 1) : 0;
  const cantAfford = !maxed && nextCost > remaining;
  const canInc = !locked && !maxed && !cantAfford;
  const canDec = rank > 0;

  return (
    <div className={`panel p-4 md:p-5 ${locked ? "opacity-60" : ""}`} data-testid={`skill-${skill.key}`}>
      <div className="flex items-start gap-3 mb-3">
        <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: skill.color }} />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-display text-lg md:text-xl" style={{ color: skill.color, textShadow: `0 0 12px ${skill.color}66` }} data-testid={`skill-${skill.key}-value`}>
              {skill.display(rank)}
            </span>
            <span className="font-display text-base md:text-lg text-[var(--t-text)] uppercase tracking-[0.04em]">{skill.name}</span>
            {locked && <span className="font-mono text-[10px] tracking-[0.2em] text-[var(--t-text-muted)] uppercase">// unlocks at lvl {skill.unlock}</span>}
            {!locked && skill.unlock > 1 && <span className="font-mono text-[10px] tracking-[0.2em] text-[var(--t-text-muted)] uppercase">// lvl {skill.unlock}+</span>}
          </div>
          <div className="text-xs md:text-sm text-[var(--t-text-dim)] mt-1 prose-t" dangerouslySetInnerHTML={{ __html: renderMarkdown(skill.desc) }} />
        </div>
      </div>

      <div className="flex items-center gap-3">
        {/* Rank segments */}
        <div className="flex gap-1 flex-1" data-testid={`skill-${skill.key}-segments`}>
          {Array.from({ length: skill.max }).map((_, i) => (
            <div
              key={`${skill.key}-seg-${i}`}
              className="h-6 flex-1 border transition"
              style={{
                background: i < rank ? skill.color : "rgba(5,12,28,0.7)",
                borderColor: i < rank ? skill.color : "var(--t-border)",
                boxShadow: i < rank ? `0 0 8px ${skill.color}80, inset 0 0 4px ${skill.color}40` : "none",
              }}
            />
          ))}
        </div>

        {/* +/- buttons */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            onClick={() => onDec(skill)}
            disabled={!canDec}
            className="w-9 h-9 inline-flex items-center justify-center rounded-full bg-[#1a2030] text-[var(--t-steel)] hover:bg-[#2a3245] disabled:opacity-30 disabled:cursor-not-allowed transition"
            data-testid={`skill-${skill.key}-dec`}
            aria-label={`Decrease ${skill.name}`}
          >
            <Minus className="w-4 h-4" />
          </button>
          <button
            onClick={() => onInc(skill)}
            disabled={!canInc}
            className="w-9 h-9 inline-flex items-center justify-center rounded-full disabled:opacity-30 disabled:cursor-not-allowed transition"
            style={{ background: skill.color, color: "#0a0e1a", boxShadow: canInc ? `0 0 12px ${skill.color}80` : "none" }}
            data-testid={`skill-${skill.key}-inc`}
            aria-label={`Increase ${skill.name}`}
            title={cantAfford ? `Need ${nextCost} SP` : ""}
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="mt-2 flex items-center justify-between text-[10px] font-mono tracking-[0.15em] text-[var(--t-text-muted)] uppercase">
        <span>Rank {rank} / {skill.max}</span>
        {!maxed && !locked && <span>Next: {nextCost} SP</span>}
        {maxed && <span style={{ color: skill.color }}>// maxed</span>}
      </div>
    </div>
  );
}
